import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Print as PrintIcon,
  Delete as DeleteIcon,
  ReceiptLong as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { 
  useGetRepairTicketByIdQuery, 
  useUpdateRepairTicketMutation,
  useAddRepairItemMutation,
  useRemoveRepairItemMutation,
  useDeliverAndBillRepairTicketMutation,
} from '../../../store/api/repairApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';

const STATUS_COLORS = {
  pending: 'warning',
  diagnosing: 'info',
  waiting_for_parts: 'error',
  repaired: 'success',
  delivered: 'primary',
  cancelled: 'default',
};

const STATUS_OPTIONS = [
  'pending', 'diagnosing', 'waiting_for_parts', 'repaired', 'delivered', 'cancelled'
];

export default function RepairTicketView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: ticket, isLoading } = useGetRepairTicketByIdQuery(id);
  const [updateTicket, { isLoading: isUpdating }] = useUpdateRepairTicketMutation();
  const [addRepairItem, { isLoading: isAddingItem }] = useAddRepairItemMutation();
  const [removeRepairItem, { isLoading: isRemovingItem }] = useRemoveRepairItemMutation();

  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const [productSearchOptions, setProductSearchOptions] = useState([]);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [deliverAndBill, { isLoading: isBilling }] = useDeliverAndBillRepairTicketMutation();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();

  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentMethod: 'cash',
    paidAmount: '',
    paymentAccountId: '',
    paymentReferenceNo: '',
    notes: '',
  });

  const [selectedProductId, setSelectedProductId] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemPrice, setItemPrice] = useState(0);

  useEffect(() => {
    if (productSearchData) {
      const data = productSearchData.products || (Array.isArray(productSearchData) ? productSearchData : []);
      setProductSearchOptions(prevOptions => {
        const currentId = selectedProductId;
        const currentInNew = data.find(p => p.id === currentId);
        const currentInOld = prevOptions.find(p => p.id === currentId);
        if (currentId && !currentInNew && currentInOld) {
          return [currentInOld, ...data];
        }
        return data;
      });
    }
  }, [productSearchData, selectedProductId]);

  const selectedProduct = productSearchOptions.find((p) => p.id === selectedProductId) || null;

  const handleStatusChange = async (e) => {
    try {
      await updateTicket({ id, status: e.target.value }).unwrap();
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleLaborChargeUpdate = async (value) => {
    try {
      await updateTicket({ id, laborCharge: value }).unwrap();
    } catch (err) {
      console.error('Failed to update labor charge', err);
    }
  };

  const handleAddItem = async () => {
    if (!selectedProductId || itemQuantity <= 0) return;
    try {
      await addRepairItem({
        ticketId: id,
        body: {
          productId: selectedProductId,
          quantity: itemQuantity,
          unitPrice: itemPrice
        }
      }).unwrap();
      setSelectedProductId('');
      setItemQuantity(1);
      setItemPrice(0);
    } catch (err) {
      console.error('Failed to add item', err);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeRepairItem({ ticketId: id, itemId }).unwrap();
    } catch (err) {
      console.error('Failed to remove item', err);
    }
  };

  const totalPartsCost = ticket?.items?.reduce((sum, item) => sum + parseFloat(item.subTotal || 0), 0) || 0;
  const laborCharge = parseFloat(ticket?.laborCharge || 0);
  const totalCost = totalPartsCost + laborCharge;

  const handleOpenBillingModal = () => {
    setPaymentData({
      paymentMethod: 'cash',
      paidAmount: totalCost > 0 ? String(totalCost.toFixed(2)) : '0',
      paymentAccountId: paymentAccounts?.[0]?.id || '',
      paymentReferenceNo: '',
      notes: '',
    });
    setBillingModalOpen(true);
  };

  const handleConfirmDeliverAndBill = async () => {
    try {
      const paid = paymentData.paidAmount !== '' ? parseFloat(paymentData.paidAmount) : totalCost;
      await deliverAndBill({
        id,
        paidAmount: isNaN(paid) ? totalCost : paid,
        paymentMethod: paymentData.paymentMethod,
        paymentAccountId: paymentData.paymentAccountId || undefined,
        paymentReferenceNo: paymentData.paymentReferenceNo?.trim() || undefined,
        notes: paymentData.notes?.trim() || undefined,
      }).unwrap();

      setBillingModalOpen(false);
    } catch (err) {
      console.error('Failed to deliver and bill ticket:', err);
      alert(err?.data?.message || 'Failed to complete billing and delivery');
    }
  };

  const handleActionClick = async () => {
    try {
      if (ticket.status === 'repaired') {
        handleOpenBillingModal();
      } else {
        await updateTicket({ id, status: 'repaired' }).unwrap();
      }
    } catch (err) {
      console.error('Failed to update ticket status', err);
      alert(err?.data?.message || 'Failed to update ticket status');
    }
  };

  if (isLoading) return <CircularProgress />;
  if (!ticket) return <Typography color="error">Ticket not found</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {ticket.ticketNumber} - Technician Workstation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.open(`/repairs/${id}/print`, '_blank')}
          >
            Print Slip
          </Button>
          <Button variant="outlined" onClick={() => navigate('/repairs')}>
            Back to List
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Details */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Status</Typography>
            <TextField
              select
              fullWidth
              value={ticket.status}
              onChange={handleStatusChange}
              disabled={isUpdating}
            >
              {STATUS_OPTIONS.map(status => (
                <MenuItem key={status} value={status}>
                  {status.replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Device Info</Typography>
            <Typography variant="body2" color="text.secondary">Model</Typography>
            <Typography variant="body1" gutterBottom>{ticket.deviceModel}</Typography>
            
            <Typography variant="body2" color="text.secondary">IMEI / Serial</Typography>
            <Typography variant="body1" gutterBottom>{ticket.deviceImei || '-'}</Typography>

            <Typography variant="body2" color="text.secondary">Fault</Typography>
            <Typography variant="body1" gutterBottom>{ticket.faultDescription}</Typography>

            <Typography variant="body2" color="text.secondary">Condition</Typography>
            <Typography variant="body1">{ticket.deviceCondition}</Typography>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Customer</Typography>
            <Typography variant="body1">{ticket.customer?.name}</Typography>
            <Typography variant="body2">{ticket.customer?.phone}</Typography>
            <Typography variant="body2">{ticket.customer?.email}</Typography>
          </Paper>
        </Grid>

        {/* Right Column: Parts & Cost */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Spare Parts Consumed</Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
              <Autocomplete
                options={productSearchOptions}
                loading={searchingProducts}
                getOptionLabel={(option) => `${option.name}${option.sku ? ` (SKU: ${option.sku})` : ''}`}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={selectedProduct}
                filterOptions={(x) => x}
                onChange={(event, newValue) => {
                  setSelectedProductId(newValue ? newValue.id : '');
                  setItemPrice(newValue ? (newValue.price || 0) : 0);
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    if (newInputValue && newInputValue.length >= 2) {
                      triggerProductSearch({ search: newInputValue, limit: 20 });
                    } else if (!newInputValue) {
                      triggerProductSearch({ limit: 20 });
                    }
                  }
                }}
                onOpen={() => {
                  if (productSearchOptions.length === 0) triggerProductSearch({ limit: 20 });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Part" size="small" />
                )}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                type="number"
                label="Qty"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(e.target.value)}
                size="small"
                sx={{ width: 100 }}
              />
              <TextField
                type="number"
                label="Price"
                value={itemPrice}
                onChange={(e) => setItemPrice(e.target.value)}
                size="small"
                sx={{ width: 120 }}
              />
              <Button 
                variant="contained" 
                onClick={handleAddItem}
                disabled={!selectedProductId || isAddingItem}
              >
                Add
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part Name</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price ({currency})</TableCell>
                    <TableCell align="right">Total ({currency})</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticket.items?.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">No parts added</TableCell></TableRow>
                  ) : (
                    ticket.items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product?.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{parseFloat(item.unitPrice).toFixed(2)}</TableCell>
                        <TableCell align="right">{parseFloat(item.subTotal).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)} disabled={isRemovingItem}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Billing Summary</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body1">Estimated Cost ({currency}):</Typography>
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                <Typography variant="body1">{parseFloat(ticket.estimatedCost).toFixed(2)}</Typography>
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <Typography variant="body1">Total Parts Cost ({currency}):</Typography>
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                <Typography variant="body1">{totalPartsCost.toFixed(2)}</Typography>
              </Grid>

              <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Labor Charge ({currency}):</Typography>
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                <TextField 
                  size="small" 
                  type="number"
                  defaultValue={laborCharge}
                  onBlur={(e) => handleLaborChargeUpdate(e.target.value)}
                  sx={{ width: 120, textAlign: 'right' }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}><Divider sx={{ my: 1 }} /></Grid>

              <Grid size={{ xs: 6 }}>
                <Typography variant="h5" color="primary">Total Cost ({currency}):</Typography>
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                <Typography variant="h5" color="primary">{totalCost.toFixed(2)}</Typography>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {ticket.status === 'delivered' ? (
                <>
                  <Chip label="Delivered to Customer" color="primary" sx={{ fontWeight: 600, py: 2, px: 1.5 }} />
                  {ticket.salesOrderId && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={() => navigate(`/sales/orders/${ticket.salesOrderId}`)}
                      >
                        View Sales Invoice
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={() => window.open(`/sales/orders/${ticket.salesOrderId}/print`, '_blank')}
                      >
                        Print Invoice
                      </Button>
                    </>
                  )}
                </>
              ) : ticket.status === 'repaired' ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PaymentIcon />}
                  onClick={handleActionClick}
                  disabled={isUpdating || isBilling}
                  sx={{ px: 3, py: 1.2, fontWeight: 700, borderRadius: 2 }}
                >
                  Deliver to Customer & Bill
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleActionClick}
                  disabled={isUpdating}
                  sx={{ px: 3, py: 1.2, fontWeight: 700, borderRadius: 2 }}
                >
                  {isUpdating ? 'Updating...' : 'Mark as Repaired'}
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Breakdown & Deliver Dialog */}
      <Dialog
        open={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 2, display: 'flex' }}>
            <PaymentIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Deliver Device & Complete Billing
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ticket: <strong>{ticket.ticketNumber}</strong> • Device: <strong>{ticket.deviceModel}</strong>
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* Customer & Bill Summary Header */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Customer:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{ticket.customer?.name || 'Walk-in'} ({ticket.customer?.phone || 'No phone'})</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Spare Parts Total:</Typography>
              <Typography variant="body2">{totalPartsCost.toFixed(2)} {currency}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Labor / Service Charge:</Typography>
              <Typography variant="body2">{laborCharge.toFixed(2)} {currency}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total Billable Amount:</Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                {totalCost.toFixed(2)} {currency}
              </Typography>
            </Box>
          </Paper>

          {/* Payment Breakdown Fields */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment Method"
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card / POS Terminal</MenuItem>
                <MenuItem value="bank">Bank Transfer</MenuItem>
                <MenuItem value="upi">UPI / Online</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="credit">Credit (On Account)</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment Account"
                value={paymentData.paymentAccountId}
                onChange={(e) => setPaymentData({ ...paymentData, paymentAccountId: e.target.value })}
                disabled={paymentData.paymentMethod === 'credit'}
              >
                <MenuItem value="">Default Cash / Bank Account</MenuItem>
                {paymentAccounts?.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name} ({acc.type})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="number"
                label={`Paid Amount (${currency})`}
                value={paymentData.paidAmount}
                onChange={(e) => setPaymentData({ ...paymentData, paidAmount: e.target.value })}
                inputProps={{ min: 0, step: '0.01' }}
                required
              />
            </Grid>

            {/* Live Balance / Change Feedback */}
            <Grid size={{ xs: 12 }}>
              {(() => {
                const paid = parseFloat(paymentData.paidAmount) || 0;
                const balance = totalCost - paid;
                if (paid >= totalCost) {
                  return (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      <strong>Payment Settled:</strong> Fully Paid.{' '}
                      {paid > totalCost && `Change Return: ${(paid - totalCost).toFixed(2)} ${currency}`}
                    </Alert>
                  );
                }
                return (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <strong>Partial / Credit Payment:</strong> Remaining balance of{' '}
                    <strong>{balance.toFixed(2)} {currency}</strong> will be recorded on customer account.
                  </Alert>
                );
              })()}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Payment Reference No. (optional)"
                placeholder="e.g. Card Auth Code, Transaction ID, Cheque #"
                value={paymentData.paymentReferenceNo}
                onChange={(e) => setPaymentData({ ...paymentData, paymentReferenceNo: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Delivery / Settlement Notes"
                placeholder="e.g. Collected by customer with charger & receipt"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setBillingModalOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmDeliverAndBill}
            disabled={isBilling}
            startIcon={isBilling ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
          >
            {isBilling ? 'Processing Bill...' : 'Confirm Delivery & Complete Bill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
